import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { getBillingStatus } from "../lib/api";
import { isUsClinicalHost } from "../lib/usClinicalHost";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Normaliza usuario de /auth/me con fallbacks de compatibilidad */
function normalizeMeUser(meUser) {
  if (!meUser) return meUser;
  return {
    ...meUser,
    account_mode: meUser.account_mode || "clinical",
  };
}

/** Prioriza campos comerciales de GET /api/billing/status sobre /auth/me */
function mergeCommercialFromBilling(meUser, billing) {
  if (!billing || typeof billing.access_allowed !== "boolean") return normalizeMeUser(meUser);
  return normalizeMeUser({
    ...meUser,
    access_allowed: billing.access_allowed,
    plan: billing.plan ?? meUser.plan,
    limits: billing.limits ?? meUser.limits,
    subscription_status: billing.subscription_status ?? meUser.subscription_status,
    trial_ends_at: billing.trial_ends_at ?? billing.trial_end ?? meUser.trial_ends_at,
    trial_end: billing.trial_end ?? billing.trial_ends_at ?? meUser.trial_end,
    current_period_end: billing.current_period_end ?? meUser.current_period_end,
    next_billing_date: billing.next_billing_date ?? meUser.next_billing_date,
  });
}

const isLocalDevAuth = () =>
  typeof window !== "undefined" &&
  process.env.REACT_APP_LOCAL_DEV_AUTH === "true" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

const LOCAL_DEV_USER = {
  nombre: "Usuario prueba",
  email: "local@test",
  plan: "lite",
  is_billing_exempt: false,
};

export const AuthProvider = ({ children }) => {
  const usHost = isUsClinicalHost();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (isLocalDevAuth() && !storedToken) {
        setUser(LOCAL_DEV_USER);
        setToken("local-dev");
        setLoading(false);
        return;
      }
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          let merged = normalizeMeUser(response.data);
          if (!usHost) {
            try {
              const billingRes = await getBillingStatus();
              merged = mergeCommercialFromBilling(response.data, billingRes.data);
            } catch {
              /* fallback: solo /me */
            }
          }
          setUser(merged);
          setToken(storedToken);
        } catch (error) {
          if (isLocalDevAuth()) {
            setUser(LOCAL_DEV_USER);
            setToken("local-dev");
          } else {
            localStorage.removeItem("token");
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [usHost]);

  const refreshUser = async () => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        let merged = normalizeMeUser(response.data);
        if (!usHost) {
          try {
            const billingRes = await getBillingStatus();
            merged = mergeCommercialFromBilling(response.data, billingRes.data);
          } catch {
            /* ignore */
          }
        }
        setUser(merged);
      } catch {
        /* ignore */
      }
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    try {
      const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
      let merged = normalizeMeUser(meRes.data);
      if (!usHost) {
        try {
          const billingRes = await getBillingStatus();
          merged = mergeCommercialFromBilling(meRes.data, billingRes.data);
        } catch {
          /* ignore */
        }
      }
      setUser(merged);
    } catch (err) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      throw err;
    }
    return response.data;
  };

  const register = async (email, password, nombre, registerOptions = {}) => {
    const {
      account_mode = "clinical",
      especialidad = "Geriatría",
      registration_token: registrationTokenOption,
    } = registerOptions;
    const registration_token =
      registrationTokenOption ||
      (typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("token")
        : null) ||
      "";
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      nombre,
      especialidad,
      account_mode,
      registration_token,
    });
    const { token: newToken } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    try {
      const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
      let merged = normalizeMeUser(meRes.data);
      if (!usHost) {
        try {
          const billingRes = await getBillingStatus();
          merged = mergeCommercialFromBilling(meRes.data, billingRes.data);
        } catch {
          /* ignore */
        }
      }
      setUser(merged);
    } catch (err) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      throw err;
    }
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const accessAllowed = user?.access_allowed !== false;
  const accessBlocked = !accessAllowed;
  const accessBlockedReason = user?.access_blocked_reason || null;
  const daysRemaining = user?.days_remaining_in_trial ?? null;

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    accessAllowed,
    accessBlocked,
    accessBlockedReason,
    daysRemaining,
    plan: user?.plan || "lite",
    isPremium: user?.plan === "premium",
    limits: user?.limits || { patients: 30, consultations: 99999 },
    subscriptionStatus: user?.subscription_status,
    accountType: user?.account_type,
    trialEndsAt: user?.trial_ends_at,
    isAdmin:
      user?.is_admin === true ||
      ["admin", "founder"].includes((user?.billing_role || user?.account_type || "").toLowerCase()),
    isBillingExempt: user?.is_billing_exempt === true,
    trialExpired: user?.trial_expired === true,
    isClinical: user?.account_mode === "clinical" || !user?.account_mode,
    isAula: user?.account_mode === "academic",
    isInstitucional: user?.account_mode === "institutional",
    login,
    register,
    logout,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
